/**
 * The signup draft — what a family should not have to retype after a refresh. (F-02, #576 o8.)
 *
 * Measured at `main` `8f7767c1`: `SignupRoute.jsx` persisted nothing, so a reload part-way through
 * signup lost the family name, the web address, the parent's name, the email and the consent tick.
 * F-02 lists "persisted state" among its acceptance items for exactly that reason.
 *
 * TWO RULES, both enforced HERE rather than at the call sites, because a call site is where they
 * get broken:
 *
 *   1. THE PASSWORD IS NEVER STORED. A draft is a convenience. A password sitting in
 *      `sessionStorage` is a credential left behind on a shared machine, and the natural way to
 *      call this module is to hand it the whole form object — so the module must be the thing that
 *      refuses, not a careful caller.
 *   2. ONLY THE KNOWN FIELDS ARE STORED, on the way in AND on the way out. An allowlist means a
 *      future field cannot arrive in storage by accident, and bytes written by an older build
 *      cannot arrive in the form by accident either.
 *
 * `sessionStorage`, not `localStorage`: a half-finished signup carries an email address and should
 * not outlive the browser session. The theme preference uses `localStorage` because it is a
 * display choice with no personal data in it — a different rule for a different thing.
 *
 * Every entry point is total. Storage can be denied outright (private mode, a locked-down browser)
 * and stored bytes can be corrupt, and neither may throw into a render: losing a draft is a small
 * disappointment, and a blank signup screen is a lost family.
 */

import { DEFAULT_CURRENCY, DEFAULT_TIMEZONE } from '~shared/locale-options.js';

/** @type {string} Where the draft lives. Exported so tests can assert the stored BYTES. */
export const DRAFT_KEY = 'chorestory.signup.draft';

/**
 * The only fields that may be persisted. `password` is deliberately, permanently absent —
 * see rule 1 above before adding to this list.
 */
const FIELDS = ['familyName', 'slug', 'name', 'email', 'consented', 'timezone', 'currency'];

/**
 * A field that starts at a value nobody chose.
 *
 * The Settings stop opens on the SERVER's own fallbacks, so a visitor who opens signup and types
 * nothing still hands this module `{timezone: 'America/Edmonton', currency: 'USD'}`. Treating those
 * as content would make every draft non-empty, which breaks `isEmptyDraft` below — and that guard
 * is the thing that makes `clearSignupDraft` stick after a successful signup. So a value equal to
 * its own default counts as nothing typed.
 */
const FIELD_DEFAULTS = { timezone: DEFAULT_TIMEZONE, currency: DEFAULT_CURRENCY };

/**
 * Copy across only the allowlisted keys that are actually present — and never a setting still
 * sitting on its own default.
 *
 * THE DEFAULT-DROP IS LOAD-BEARING, and it is here rather than at a call site because both
 * directions need it. `Signup.jsx` reads a present `defaultTimezone` as "the family chose this",
 * so a stored `America/Edmonton` turns an untouched Settings stop into an explicit value in the
 * signup request one refresh later — pinning today's server fallback, which is the exact thing
 * `createFamily` takes care not to do. Applying it on the way OUT as well repairs bytes an older
 * build already wrote.
 */
function pick(source) {
  const out = {};
  if (!source || typeof source !== 'object') return out;
  for (const field of FIELDS) {
    if (!Object.prototype.hasOwnProperty.call(source, field)) continue;
    const value = source[field];
    if (
      Object.prototype.hasOwnProperty.call(FIELD_DEFAULTS, field) &&
      value === FIELD_DEFAULTS[field]
    ) {
      continue;
    }
    out[field] = value;
  }
  return out;
}

/** An untouched form: every text field blank and consent unticked. Nothing worth keeping. */
function isEmptyDraft(draft) {
  // `pick` has already dropped every defaulted setting, so a value reaching here is one the family
  // chose. The check is kept anyway: it costs nothing, and it is the second of two places a
  // default would have to slip through.
  return FIELDS.every((field) => {
    const value = draft[field];
    if (value === undefined || value === '' || value === false || value === null) return true;
    return value === FIELD_DEFAULTS[field];
  });
}

/**
 * Save what the family has typed so far.
 *
 * AN EMPTY DRAFT IS NOT A DRAFT, and it REMOVES rather than writes. That is not tidiness — it is
 * what makes `clearSignupDraft` actually stick. Measured with a probe: after a successful signup
 * cleared the draft, the form mounted once more and its effect wrote `{familyName: '', slug: '',
 * name: '', email: '', consented: false}` straight back over the clear, from
 * `SignupRoute.jsx` -> `Signup.jsx`. The mount effect fires unconditionally and a freshly mounted
 * route cannot know a signup just succeeded, so the guard belongs HERE, where every write passes,
 * rather than in a flag that a remount resets.
 *
 * It also means a visitor who opens signup and types nothing leaves nothing behind at all.
 *
 * @param {Object} form The whole signup form. Anything outside `FIELDS` is dropped, including the
 *                      password.
 */
export function writeSignupDraft(form) {
  const draft = pick(form);
  if (isEmptyDraft(draft)) {
    clearSignupDraft();
    return;
  }
  try {
    window.sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {
    /* Storage denied. The form still works; only the draft is lost. */
  }
}

/**
 * Read the draft back.
 *
 * @returns {Object} The allowlisted fields, or `{}` when there is nothing usable. Never throws.
 */
export function readSignupDraft() {
  try {
    const raw = window.sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return {};
    return pick(JSON.parse(raw));
  } catch {
    return {};
  }
}

/** Forget the draft. Called once the family no longer needs it. */
export function clearSignupDraft() {
  try {
    window.sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    /* Storage denied. Nothing was stored either, so nothing is left behind. */
  }
}

/**
 * A failed signup email is historical, account-scoped information. Keep it only in this tab,
 * for ten minutes. Legacy records without an owner/time cannot safely be restored.
 */
export const UNSENT_NOTICE_KEY = 'chorestory.signup.unsentVerification';
export const UNSENT_NOTICE_TTL_MS = 10 * 60 * 1000;

export function writeUnsentNotice(email, actor) {
  if (typeof email !== 'string' || !email.trim() || !actor?.id || !actor?.tenantId) return;
  try {
    window.sessionStorage.setItem(UNSENT_NOTICE_KEY, JSON.stringify({
      email: email.trim(), userId: String(actor.id), tenantId: String(actor.tenantId), createdAt: Date.now(),
    }));
  } catch {
    /* Storage denied: the fresh warning still works. */
  }
}

/** Read only complete, recent records. Storage is a convenience, never identity evidence. */
export function readUnsentNotice() {
  try {
    const raw = window.sessionStorage.getItem(UNSENT_NOTICE_KEY);
    if (!raw) return null;
    const notice = JSON.parse(raw);
    const age = Date.now() - notice?.createdAt;
    if (typeof notice?.email !== 'string' || !notice.email.trim()
      || typeof notice.userId !== 'string' || !notice.userId
      || typeof notice.tenantId !== 'string' || !notice.tenantId
      || !Number.isFinite(notice.createdAt) || age < 0 || age >= UNSENT_NOTICE_TTL_MS) {
      clearUnsentNotice();
      return null;
    }
    return notice;
  } catch {
    clearUnsentNotice();
    return null;
  }
}

export function clearUnsentNotice() {
  try { window.sessionStorage.removeItem(UNSENT_NOTICE_KEY); }
  catch { /* Storage denied. */ }
}
